import { useTimeZone } from '@/auth/useTimeZone';
import { InfoPage } from '@/components/info-page';
import { SessionScheduleCalendar, eventsFromSessions } from '@/components/session/session-schedule-calendar';
import { getCoachSessions } from '@/lib/api-calls';
import { useErrorToast } from '@/lib/error-handling';
import { fetchAllPages } from '@/lib/pagination';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing';
import { isCoach } from '@airreading/common';
import { instantFromStringWithDefault, type InstantInterval } from '@airreading/common';
import { CoachSessionRemarkV2 } from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

const calendarViewSchema = z.enum(['month', 'week', 'work_week', 'day', 'agenda']);

export const Route = createFileRoute('/_layout/my-classes/calendar')({
  beforeLoad: validateRouteRole(isCoach),
  component: MyClassesCalendar,
  validateSearch: z.object({
    view: calendarViewSchema.optional(),
    date: z.string().optional(),
  }),
});

function MyClassesCalendar() {
  const { timeZone } = useTimeZone();
  const [instantInterval, setInstantInterval] = useState<InstantInterval | null>(null);
  const { data, isPending, error } = useQuery(
    getCoachSessions({
      ...(instantInterval && {
        startOnOrAfter: instantInterval.start.toString(),
        startOnOrBefore: instantInterval.end.toString(),
        sessionRemarksV2: [
          CoachSessionRemarkV2.canceledWithin12Hours,
          CoachSessionRemarkV2.endEarly,
          CoachSessionRemarkV2.lateStart,
          CoachSessionRemarkV2.subClass,
          CoachSessionRemarkV2.subClassWithin12Hours,
        ].join(','),
        includeSessionsWithoutSessionRemarksV2: 'true',
      }),
      ...fetchAllPages, // fetch all sessions within the date range
    }),
  );
  useErrorToast(error ?? null, 'Error loading classes. Please try again.');
  const sessions = data?.coachSessions;
  const { events, sessionById } = useMemo(() => eventsFromSessions(sessions ?? [], timeZone), [sessions, timeZone]);
  const [view, setView] = useNavigateSearchState(Route.id, 'view');
  const [instantQueryString, setInstantQueryString] = useNavigateSearchState(Route.id, 'date');
  const [activeInstant, setActiveInstant] = [
    useMemo(() => instantFromStringWithDefault(instantQueryString, Temporal.Now.instant()), [instantQueryString]),
    useCallback(
      (newInstant: Temporal.Instant) => {
        setInstantQueryString(newInstant.toString());
      },
      [setInstantQueryString],
    ),
  ];

  return (
    <InfoPage headerText="My classes" data-testid="sessions-calendar-page">
      <div style={{ height: '85vh' }}>
        <SessionScheduleCalendar
          isLoading={isPending}
          events={events}
          sessionById={sessionById}
          activeInstant={activeInstant}
          setActiveInstant={setActiveInstant}
          onRangeChange={setInstantInterval}
          views={['month', 'week', 'day']}
          view={view ?? 'week'}
          onView={setView}
        />
      </div>
    </InfoPage>
  );
}
